import React from "react";
import { Col, Divider, Input, Row, Form, Select } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { patientFields } from "../fieldNames";
import { GRAY_500 } from "../../../../../../../constant/colors";
import { emailInputRules, onlyWhitespacesRule, zipInputRules } from "../../../../utils";
import { warnOnHyphenSpaceOrInitials } from "../../../../utils/warningValidators";
import { phoneNumberLengthRegEx } from "../../../../../../../utils/formValidation";
import {
  DobFormItem,
  FormDividerText,
  FormItem,
  FormItemWithWarning,
  InputPhoneWithPhoneTypeAddon
} from "../../../../components";
import { SingleSelect } from "../../../../../../customComponent/CustomSelect";
import { States as STATES } from "../../../../../../../constant/states";
import { stateOptionsSearch } from "../../../../../../../utils/search";
import { GenderFormItem } from "../../../../GenderFormItem";
import { UnifiedCaregiverSection } from "../../../../components/form/UnifiedCaregiverSection";
import genentechCopayConfigurations from "../../configurations";
import { vendors } from "../../../../formSectionsController";

const { Option } = Select;

const rowWithWarningStyle = {
  display: "flex",
  alignItems: "flex-start"
};

export default function GenentechCopayPatient() {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const gender = Form.useWatch(patientFields.gender, form);
  const warningMessage = t("application_form_editor.genentech.copay.warnings.name_matches_insurance_or_id");

  return (
    <>
      <div id="enrollment-form-patient-information" />
      <Divider orientation="left" orientationMargin="0" style={{ color: GRAY_500 }}>
        <FormDividerText>{t("application_form_editor.genentech.copay.patient.header")}</FormDividerText>
      </Divider>

      <Form.Item name={patientFields.id} hidden />

      <Row gutter={14} style={rowWithWarningStyle}>
        <Col span={6}>
          <FormItemWithWarning
            name={patientFields.firstName}
            label={t("application_form_editor.genentech.copay.patient.labels.first_name")}
            rules={[{ required: true }, onlyWhitespacesRule]}
            warningValidator={warnOnHyphenSpaceOrInitials}
            warningMessage={warningMessage}
          >
            <Input placeholder={t("application_form_editor.genentech.copay.patient.placeholders.first_name")} />
          </FormItemWithWarning>
        </Col>
        <Col span={6}>
          <FormItemWithWarning
            name={patientFields.middleName}
            label={t("application_form_editor.genentech.copay.patient.labels.middle_name")}
            rules={[onlyWhitespacesRule]}
            warningValidator={warnOnHyphenSpaceOrInitials}
            warningMessage={warningMessage}
          >
            <Input placeholder={t("application_form_editor.genentech.copay.patient.placeholders.middle_name")} />
          </FormItemWithWarning>
        </Col>
        <Col span={6}>
          <FormItemWithWarning
            rules={[{ required: true }, onlyWhitespacesRule]}
            name={patientFields.lastName}
            label={t("application_form_editor.genentech.copay.patient.labels.last_name")}
            warningValidator={warnOnHyphenSpaceOrInitials}
            warningMessage={warningMessage}
          >
            <Input placeholder={t("application_form_editor.genentech.copay.patient.placeholders.last_name")} />
          </FormItemWithWarning>
        </Col>
        <Col span={6}>
          <GenderFormItem
            name={patientFields.gender}
            label={t("application_form_editor.genentech.copay.patient.labels.gender")}
            showError={!gender}
          />
        </Col>
      </Row>
      <Row gutter={14}>
        <Col span={6}>
          <DobFormItem
            required
            name={patientFields.dob}
            label={t("application_form_editor.genentech.copay.patient.labels.dob")}
          />
        </Col>
        <Col span={6}>
          <FormItem
            rules={[{ required: true }, onlyWhitespacesRule]}
            name={patientFields.address}
            label={t("application_form_editor.genentech.copay.patient.labels.street_address")}
          >
            <Input placeholder={t("application_form_editor.genentech.copay.patient.placeholders.street_address")} />
          </FormItem>
        </Col>
        <Col span={6}>
          <FormItem
            name={patientFields.city}
            label={t("application_form_editor.genentech.copay.patient.labels.city")}
            rules={[{ required: true }, onlyWhitespacesRule]}
          >
            <Input placeholder={t("application_form_editor.genentech.copay.patient.placeholders.city")} />
          </FormItem>
        </Col>
        <Col span={6}>
          <FormItem
            rules={[{ required: true }]}
            name={patientFields.state}
            label={t("application_form_editor.genentech.copay.patient.labels.state")}
          >
            <SingleSelect
              useCommonStyle={false}
              bordered={true}
              placeholder={t("application_form_editor.genentech.copay.patient.placeholders.state")}
              filterOption={stateOptionsSearch}
            >
              {Object.values(STATES).map((state, index) => (
                <Option key={index} value={state.code}>
                  {state.name}
                </Option>
              ))}
            </SingleSelect>
          </FormItem>
        </Col>
      </Row>
      <Row gutter={14}>
        <Col span={8}>
          <FormItem
            rules={[{ required: true }, ...zipInputRules]}
            name={patientFields.zip}
            label={t("application_form_editor.genentech.copay.patient.labels.zip_code")}
          >
            <Input
              maxLength={5}
              placeholder={t("application_form_editor.genentech.copay.patient.placeholders.zip_code")}
            />
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem
            rules={emailInputRules}
            name={patientFields.email}
            label={t("application_form_editor.genentech.copay.patient.labels.email")}
          >
            <Input
              addonBefore={<MailOutlined />}
              placeholder={t("application_form_editor.genentech.copay.patient.placeholders.email")}
            />
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem
            rules={[{ required: true }, { pattern: phoneNumberLengthRegEx, message: "" }]}
            name={patientFields.phone}
            label={t("application_form_editor.genentech.copay.patient.labels.phone_number")}
          >
            <InputPhoneWithPhoneTypeAddon
              style={{ width: "100%" }}
              placeholder={t("application_form_editor.genentech.copay.patient.placeholders.phone_number")}
              typeName={patientFields.phoneType}
            />
          </FormItem>
        </Col>
      </Row>

      <UnifiedCaregiverSection
        patientDobField={patientFields.dob}
        drugNameField={null}
        formConfiguration={genentechCopayConfigurations}
        featureFlagKey="fe.caregiver-under-18-enrollment"
        includeAddress={true}
        vendor={vendors.GENENTECH_COPAY}
      />
    </>
  );
}
